import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { BAR_GAP, MIN_CHART_HEIGHT } from '../constants';

/**
 * Main card wrapper — matches other community-stats cards
 */
export const StyledCard = styled(Stack)(({ theme }) => ({
  flex: 1,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.common.black
      : theme.palette.common.white,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.customShape.borderRadiusXLarge,
  padding: `${theme.spacing(4)} ${theme.spacing(6)}`,
  gap: theme.spacing(4),

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4),
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    boxSizing: 'border-box',
  },
}));

/**
 * Header row: title group on left, toggle on right
 */
export const StyledHeaderContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}));

/**
 * Title + info icon row
 */
export const StyledTitleContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const StyledTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 500,
  lineHeight: 1.5,
  letterSpacing: '0.08px',
  color: theme.palette.text.primary,
}));

export const StyledInfoButton = styled(IconButton)({
  padding: 0,
});

/**
 * Overall | Net segmented toggle container
 */
export const StyledToggleGroup = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
});

interface ToggleButtonProps {
  active: boolean;
  position: 'left' | 'right';
}

export const StyledViewToggleButton = styled(Button)<ToggleButtonProps>(
  ({ theme, active, position }) => ({
    height: 32,
    minWidth: 0,
    padding: theme.spacing(0, 4),
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.07px',
    textTransform: 'none',
    color: active ? theme.palette.text.primary : theme.palette.text.secondary,
    backgroundColor: active ? 'rgba(170, 178, 255, 0.24)' : 'transparent',
    border: `1px solid ${theme.palette.info.main}`,
    borderRadius: 0,
    borderLeft: position === 'right' ? 'none' : undefined,
    borderTopLeftRadius: position === 'left' ? 30 : 0,
    borderBottomLeftRadius: position === 'left' ? 30 : 0,
    borderTopRightRadius: position === 'right' ? 30 : 0,
    borderBottomRightRadius: position === 'right' ? 30 : 0,

    '&:hover': {
      backgroundColor: active
        ? 'rgba(170, 178, 255, 0.35)'
        : theme.palette.action.hover,
    },
  }),
);

/**
 * Thin divider line between sections — marginTop: auto pushes it (and the
 * educational section below it) to the bottom of the card so both cards'
 * dividers stay aligned regardless of content height differences.
 */
export const StyledDivider = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '1px',
  backgroundColor: theme.palette.divider,
  marginTop: 'auto',
}));

/**
 * Overall/Net view container — prevents layout jump when toggling (matches Open Orders pattern)
 */
export const StyledViewContainer = styled(Box)(() => ({
  position: 'relative',
  overflow: 'hidden',
  minHeight: MIN_CHART_HEIGHT,
  maxHeight: MIN_CHART_HEIGHT,
  display: 'flex',
  flexDirection: 'column',
}));

interface FadeWrapperProps {
  isPositioned?: boolean;
}

/**
 * Wrapper for Fade content — absolute when inactive so only active view affects layout
 */
export const StyledFadeWrapper = styled(Box)<FadeWrapperProps>(
  ({ isPositioned = false }) => ({
    position: isPositioned ? 'absolute' : 'relative',
    top: 0,
    left: 0,
    right: 0,
    ...(!isPositioned && { flex: 1, minHeight: 0 }),
  }),
);

/**
 * Chart area — flex container with chart + price axis.
 * flex: 1 ensures it grows to match Open Orders card height when in same row.
 */
export const StyledChartWrapper = styled(Stack)(() => ({
  flexDirection: 'column',
  flex: 1,
  minHeight: MIN_CHART_HEIGHT,
  overflow: 'hidden',
}));

/**
 * Chart column — wraps chart row and percentage axis
 */
export const StyledChartColumn = styled(Stack)(() => ({
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  flexDirection: 'column',
}));

/**
 * Chart row — bar container + price axis side by side (8px gap per Figma)
 */
export const StyledChartRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flex: 1,
  minHeight: 0,
  gap: theme.spacing(2),
  position: 'relative',
  marginBottom: theme.spacing(2),
}));

/**
 * Chart labels row — Short | Long — static flow at top of chart area (per Figma)
 */
export const StyledChartLabelsRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 4),
  paddingRight: `calc(${theme.spacing(4)} + 59px + ${theme.spacing(2)})`,
  marginBottom: theme.spacing(2),
  minHeight: theme.spacing(3.25),
  flexShrink: 0,
}));

interface ChartLabelProps {
  align?: 'left' | 'right';
}

export const StyledChartLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'align',
})<ChartLabelProps>(({ theme, align = 'left' }) => ({
  flex: 1,
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 600,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.secondary,
  textAlign: align === 'right' ? 'right' : 'left',
}));

/**
 * Bar rows container — scrollable column of rows
 * Center vertical divider line per Figma
 */
export const StyledBarRowsContainer = styled(Stack)(({ theme }) => ({
  flex: 1,
  flexDirection: 'column',
  gap: 0,
  minHeight: 0,
  position: 'relative',
  padding: theme.spacing(0, 4),
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '50%',
    top: 0,
    bottom: 0,
    width: 1,
    backgroundColor: theme.palette.divider,
    transform: 'translateX(-50%)',
    pointerEvents: 'none',
    zIndex: 1,
  },
}));

/**
 * Horizontal dotted grid line at price tick levels
 */
interface GridLineProps {
  topPercent: number;
}

export const StyledGridLine = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'topPercent',
})<GridLineProps>(({ theme, topPercent }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  top: `${topPercent}%`,
  height: 2,
  border: 'none',
  backgroundImage: `radial-gradient(circle, ${theme.palette.divider} 1px, transparent 1px)`,
  backgroundSize: '8px 2px',
  backgroundRepeat: 'repeat-x',
  pointerEvents: 'none',
  zIndex: 0,
}));

/**
 * Single bar row — left half (short) | gap | right half (long)
 */
export const StyledBarRow = styled(Stack)(() => ({
  flexDirection: 'row',
  flex: 1,
  minHeight: 0,
  gap: BAR_GAP,
  position: 'relative',
  zIndex: 1,
}));

/**
 * Short bar half — bar grows left from center
 */
export const StyledShortBarHalf = styled(Stack)({
  flex: 1,
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  minWidth: 0,
});

/**
 * Long bar half — bar grows right from center
 */
export const StyledLongBarHalf = styled(Stack)({
  flex: 1,
  justifyContent: 'flex-end',
  alignItems: 'flex-start',
  minWidth: 0,
});

interface BarSegmentProps {
  widthPercent: number;
  variant: 'short' | 'long';
  isBelow?: boolean;
}

const SHORT_COLOR = '#8458FC';
const LONG_COLOR = '#F9C509';
const CURRENT_PRICE_COLOR = '#0DE77E';

export const StyledBarSegment = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'widthPercent' && prop !== 'variant' && prop !== 'isBelow',
})<BarSegmentProps>(({ theme, widthPercent, variant, isBelow }) => {
  const isDark = theme.palette.mode === 'dark';
  const transparentLight = isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.3)';
  const transparentMedium = isDark
    ? 'rgba(0,0,0,0.6)'
    : 'rgba(255,255,255,0.6)';
  const secondaryInvert = isDark
    ? 'rgba(132,88,252,0.5)'
    : 'rgba(62,38,128,0.5)';

  const shortGradientNormal = `linear-gradient(90deg, ${transparentLight} 0%, ${secondaryInvert} 100%), linear-gradient(90deg, ${SHORT_COLOR} 0%, ${SHORT_COLOR} 100%)`;
  // Below price: short bar uses LONG_COLOR (mirrored fade: transparent at outer tip → yellow at center)
  const shortGradientBelow = `linear-gradient(90deg, ${transparentMedium} 0%, ${LONG_COLOR} 100%), linear-gradient(90deg, ${LONG_COLOR} 0%, ${LONG_COLOR} 100%)`;
  const longGradientNormal = `linear-gradient(90deg, ${LONG_COLOR} 0%, ${transparentMedium} 100%), linear-gradient(90deg, ${LONG_COLOR} 0%, ${LONG_COLOR} 100%)`;
  // Below price: long bar uses SHORT_COLOR (purple at center → transparent at outer tip)
  const longGradientBelow = `linear-gradient(90deg, ${secondaryInvert} 0%, ${transparentLight} 100%), linear-gradient(90deg, ${SHORT_COLOR} 0%, ${SHORT_COLOR} 100%)`;

  const shortGradient = isBelow ? shortGradientBelow : shortGradientNormal;
  const longGradient = isBelow ? longGradientBelow : longGradientNormal;

  return {
    height: '100%',
    minHeight: 2,
    width: `${Math.max(0, Math.min(100, widthPercent))}%`,
    backgroundImage: variant === 'short' ? shortGradient : longGradient,
    flexShrink: 0,
  };
});

/**
 * Percentage axis labels at bottom (24px horizontal padding per Figma)
 */
export const StyledPercentageAxis = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  padding: theme.spacing(0, 6),
  paddingRight: `calc(${theme.spacing(6)} + 59px + ${theme.spacing(2)})`,
  height: 29,

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 4),
    paddingRight: `calc(${theme.spacing(4)} + 59px + ${theme.spacing(2)})`,
  },
}));

export const StyledPercentageTick = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.third,
}));

/**
 * Price axis — separate column to the right of chart (per Figma: 59px wide, 4px padding)
 */
export const StyledPriceAxis = styled(Stack)(({ theme }) => ({
  position: 'relative',
  alignItems: 'flex-end',
  width: 59,
  padding: theme.spacing(0, 0.5),
  flexShrink: 0,
}));

interface PriceTickProps {
  topPercent: number;
}

export const StyledPriceTick = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'topPercent',
})<PriceTickProps>(({ theme, topPercent }) => ({
  position: 'absolute',
  top: `${topPercent}%`,
  transform: 'translateY(-50%)',
  right: theme.spacing(0.5),
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.third,
  whiteSpace: 'nowrap',
}));

/**
 * Current price indicator — horizontal line + badge
 */
interface CurrentPriceWrapperProps {
  topPercent: number;
}

export const StyledCurrentPriceWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'topPercent',
})<CurrentPriceWrapperProps>(({ topPercent }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  top: `${topPercent}%`,
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
  pointerEvents: 'none',
  zIndex: 3,
}));

/**
 * Tag-shaped badge with right-pointing arrow (pill + chevron)
 * Rounded left corners via border-radius, arrow tip via ::after pseudo-element
 */
export const StyledCurrentPriceBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.25, 1, 0.25, 1.5),
  backgroundColor: CURRENT_PRICE_COLOR,
  flexShrink: 0,
  borderRadius: `${theme.customShape.borderRadiusSmall}px`,
  position: 'relative',
  marginRight: 10,
  '&::after': {
    content: '""',
    position: 'absolute',
    right: -10,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 0,
    height: 0,
    borderTop: '10px solid transparent',
    borderBottom: '10px solid transparent',
    borderLeft: `10px solid ${CURRENT_PRICE_COLOR}`,
  },
}));

export const StyledCurrentPriceText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 600,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.primary,
}));

export const StyledCurrentPriceLine = styled(Box)({
  flex: 1,
  height: 0,
  borderTop: `2px solid ${CURRENT_PRICE_COLOR}`,
});

/**
 * Tooltip styling constants for use with MUI Tooltip slotProps
 */
const TOOLTIP_BG_DARK = 'rgba(0, 0, 0, 0.85)';
const TOOLTIP_BG_LIGHT = 'rgba(255, 255, 255, 0.95)';

export const barTooltipStyles = {
  tooltip: (isDark: boolean) => ({
    backgroundColor: isDark ? TOOLTIP_BG_DARK : TOOLTIP_BG_LIGHT,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    padding: '8px 16px',
    minWidth: 120,
  }),
  arrow: (isDark: boolean) => ({
    color: isDark ? TOOLTIP_BG_DARK : TOOLTIP_BG_LIGHT,
  }),
};

export const StyledBarTooltipPrice = styled(Typography)(({ theme }) => ({
  display: 'block',
  marginBottom: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  fontSize: theme.typography.pxToRem(12),
}));

export const StyledBarTooltipShort = styled(Typography)(({ theme }) => ({
  display: 'block',
  color: theme.palette.primary.main,
  fontSize: theme.typography.pxToRem(12),
}));

export const StyledBarTooltipLong = styled(Typography)(({ theme }) => ({
  display: 'block',
  color: theme.palette.warning.main,
  fontSize: theme.typography.pxToRem(12),
}));

/**
 * Loading / no-data container
 */
export const StyledLoadingContainer = styled(Stack)(({ theme }) => ({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: theme.spacing(75),
}));
